import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  RiFileTextLine,
  RiDownloadLine,
  RiSendPlaneFill,
  RiFileListLine,
  RiCalendarEventLine,
  RiTimeLine,
  RiMailLine,
  RiGlobalLine,
  RiInformationLine,
  RiFileCopyLine,
  RiShieldCheckLine,
  RiAlarmLine,
  RiCalendarLine,
  RiMicroscopeLine,
  RiUploadCloudLine,
  RiFilePdfLine,
  RiFileWordLine,
  RiPresentationLine,
  RiCheckLine,
  RiArrowDownSLine,
} from 'react-icons/ri';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { API_BASE } from '../config';
import { useSummit } from './summitdetails';

axios.defaults.baseURL = API_BASE;

// ─── Theme ─────────────────────────────────────────────
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

// ─── CSS ──────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:clamp(10px,0.8vw,11px);font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:clamp(12px,2vw,20px)}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:clamp(10px,1.5vw,14px)}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(0.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:12px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0;flex-wrap:wrap}
  .t-st{background:${T.white};padding:clamp(14px,2vw,20px) clamp(18px,3vw,28px);display:flex;flex-direction:column;gap:4px;min-width:70px;flex:1}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.2vw,1.8rem);font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:clamp(9px,0.7vw,11px);color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.06em}

  .t-card{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};padding:clamp(16px,2.5vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-3px);box-shadow:${T.shadowLg}}
  .t-card-header{display:flex;align-items:center;gap:10px;margin-bottom:16px;flex-wrap:wrap}
  .t-card-icon{width:clamp(36px,4vw,44px);height:clamp(36px,4vw,44px);border-radius:50%;background:${T.softTeal};display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .t-card-title{font-size:clamp(18px,1.6vw,20px);font-weight:800;color:${T.text}}

  .t-form-input{width:100%;padding:clamp(12px,1.5vw,14px) clamp(14px,1.8vw,18px);background:${T.offWhite};border:1px solid ${T.borderLight};border-radius:12px;font-size:clamp(13px,1.1vw,14px);color:${T.text};transition:all 0.2s;outline:none;font-family:'Inter',sans-serif}
  .t-form-input:focus{border-color:${T.primary};background:${T.white}}
  .t-form-label{display:block;font-size:clamp(12px,1vw,13px);font-weight:600;color:${T.textMid};margin-bottom:6px}
  .t-required{color:#EF4444}

  .t-btn-primary{background:${T.gradGold};color:${T.navyDeep};border:none;padding:clamp(12px,1.5vw,14px) clamp(24px,3vw,32px);border-radius:50px;font-size:clamp(14px,1.2vw,15px);font-weight:700;cursor:pointer;transition:all 0.2s;width:100%;display:flex;align-items:center;justify-content:center;gap:8px}
  .t-btn-primary:hover:not(:disabled){transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-btn-primary:disabled{opacity:0.6;cursor:not-allowed}
  .t-btn-outline{background:${T.white};color:${T.primary};border:1px solid ${T.borderLight};padding:clamp(8px,1vw,10px) clamp(18px,2vw,24px);border-radius:40px;font-size:clamp(13px,1.1vw,14px);font-weight:600;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:6px;font-family:'Inter',sans-serif}
  .t-btn-outline:hover{background:${T.primary};color:${T.white};border-color:${T.primary}}

  .t-topic-tag{background:${T.softTeal};color:${T.primary};padding:clamp(4px,0.6vw,6px) clamp(10px,1.5vw,16px);border-radius:30px;font-size:clamp(12px,1vw,13px);font-weight:500;transition:all 0.2s}
  .t-dropzone{border:2px dashed ${T.borderLight};border-radius:14px;padding:clamp(16px,2vw,24px);text-align:center;background:${T.offWhite};transition:all 0.2s;cursor:pointer}
  .t-dropzone:hover{border-color:${T.primary};background:${T.softTeal}}
  .t-dropzone-active{border-color:${T.primary};background:${T.softTeal}}

  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:clamp(16px,2vw,28px) clamp(12px,4vw,64px);margin-top:clamp(24px,3vw,40px)}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:clamp(10px,0.8vw,12px);color:${T.textMuted};letter-spacing:.01em}

  @keyframes spin{to{transform:rotate(360deg)}}
  .t-spinner{width:18px;height:18px;border:2px solid ${T.navyDeep};border-top-color:transparent;border-radius:50%;animation:spin 0.8s linear infinite}

  .t-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:clamp(20px,3vw,32px);margin-bottom:clamp(32px,4vw,48px)}
  .t-form-2{display:grid;grid-template-columns:1fr 1fr;gap:clamp(16px,2vw,24px);margin-bottom:clamp(16px,2vw,24px)}
  .t-guidelines-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(14px,2vw,20px)}
  .t-dates-list{display:flex;flex-direction:column;gap:clamp(10px,1.5vw,14px)}
  .t-topics-wrap{display:flex;flex-wrap:wrap;gap:clamp(8px,1vw,12px)}
  .t-success-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(12px,1.5vw,16px)}

  @media(max-width:900px){
    .t-grid-2{grid-template-columns:1fr}
    .t-form-2{grid-template-columns:1fr}
    .t-guidelines-grid{grid-template-columns:1fr}
    .t-success-grid{grid-template-columns:1fr}
  }
  @media(max-width:768px){
    .t-hero-i{flex-direction:column;align-items:stretch}
    .t-stats{width:100%}
    .t-form-2{grid-template-columns:1fr}
    .t-guidelines-grid{grid-template-columns:1fr}
    .t-success-grid{grid-template-columns:1fr}
  }
  @media(max-width:480px){
    .t-hero{padding:20px 12px 24px}
    .t-hero-h{font-size:1.4rem}
    .t-hero-p{font-size:0.85rem}
    .t-stats{flex-direction:column}
    .t-st{padding:10px 14px;min-width:auto}
    .t-st-n{font-size:1.2rem}
    .t-card{padding:14px}
    .t-card-header{gap:8px}
    .t-card-icon{width:32px;height:32px}
    .t-card-icon svg{width:18px;height:18px}
    .t-card-title{font-size:16px}
    .t-form-input{padding:10px 14px;font-size:13px}
    .t-dropzone{padding:14px}
    .t-btn-primary{font-size:14px;padding:12px 20px}
    .t-btn-outline{font-size:12px;padding:6px 14px}
    .t-topic-tag{font-size:11px;padding:3px 10px}
    .t-dates-list{gap:8px}
  }
  @media(max-width:360px){
    .t-hero-h{font-size:1.2rem}
    .t-card-title{font-size:14px}
    .t-form-input{font-size:12px;padding:8px 12px}
    .t-btn-primary{font-size:13px;padding:10px 16px}
  }
`;

const guidelines = [
  { title: "Abstract Length", desc: "Maximum 500 words (excluding title and authors)", icon: RiFileTextLine },
  { title: "Language", desc: "English only — clear, concise scientific writing", icon: RiGlobalLine },
  { title: "Structure", desc: "Title, Authors, Affiliations, Body, Keywords, References", icon: RiFileListLine },
  { title: "Figures/Tables", desc: "Maximum 2 figures or tables (optional)", icon: RiInformationLine },
  { title: "Formatting", desc: "Times New Roman, 12pt, single-spaced", icon: RiFileCopyLine },
  { title: "Originality", desc: "Must be original, unpublished work", icon: RiShieldCheckLine },
];

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ACCEPTED_FILE_EXT = '.pdf,.doc,.docx';

const isWordFile = (file) =>
  file?.type === 'application/msword' ||
  file?.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
  /\.(doc|docx)$/i.test(file?.name || '');

// ─── MULTI-SELECT ──────────────────────────────────────
const MultiSelect = ({ options, selected, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (disabled) return;
    const newSelected = selected.includes(value)
      ? selected.filter(id => id !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const selectedNames = options
    .filter(opt => selected.includes(opt._id))
    .map(opt => opt.name);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'clamp(10px,1.5vw,14px) clamp(12px,1.8vw,18px)',
          background: T.offWhite,
          border: `1px solid ${T.borderLight}`,
          borderRadius: 12,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          minHeight: 'clamp(44px,5vw,52px)',
          flexWrap: 'wrap',
          gap: '4px',
        }}
        className="t-form-input"
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
          {selectedNames.length > 0 ? (
            selectedNames.map((name, idx) => (
              <span
                key={idx}
                style={{
                  background: T.softTeal,
                  color: T.primary,
                  padding: '2px 10px',
                  borderRadius: 20,
                  fontSize: 'clamp(11px,0.9vw,13px)',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {name}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    const id = options.find(o => o.name === name)?._id;
                    if (id) toggleOption(id);
                  }}
                  style={{
                    cursor: 'pointer',
                    marginLeft: 2,
                    fontSize: 'clamp(12px,1vw,14px)',
                    lineHeight: 1,
                    color: T.primary,
                  }}
                >
                  ×
                </span>
              </span>
            ))
          ) : (
            <span style={{ color: T.textMuted, fontSize: 'clamp(12px,1vw,14px)' }}>{placeholder || 'Select...'}</span>
          )}
        </div>
        <RiArrowDownSLine
          size={18}
          style={{
            color: T.textMuted,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}
        />
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: T.white,
            border: `1px solid ${T.borderLight}`,
            borderRadius: 12,
            boxShadow: T.shadowMd,
            maxHeight: 200,
            overflowY: 'auto',
            zIndex: 100,
            padding: '6px 0',
          }}
        >
          {options.map(option => {
            const isSelected = selected.includes(option._id);
            return (
              <div
                key={option._id}
                onClick={() => toggleOption(option._id)}
                style={{
                  padding: 'clamp(8px,1vw,10px) clamp(14px,1.8vw,18px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  background: isSelected ? T.softTeal : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = T.offWhite;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  style={{ accentColor: T.primary, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.text }}>{option.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────
export default function Abstract() {
  const { eventId, eventName } = useSummit();

  // ── Fetch event name (robust) ──
  const [fetchedEventName, setFetchedEventName] = useState('');
  const [loadingEventName, setLoadingEventName] = useState(true);

  useEffect(() => {
    const fetchEventName = async () => {
      if (!eventId) {
        setFetchedEventName('Conference');
        setLoadingEventName(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Try different possible response structures
        const event =
          response.data?.event ||
          response.data?.data ||
          response.data;
        const title = event?.title || event?.name || null;
        if (title) {
          setFetchedEventName(title);
        } else {
          setFetchedEventName('Conference');
        }
      } catch (error) {
        console.error('Error fetching event name:', error);
        setFetchedEventName('Conference');
      } finally {
        setLoadingEventName(false);
      }
    };
    fetchEventName();
  }, [eventId]);

  // ── State ──
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    fullName: '',
    email: '',
    abstractTitle: '',
    presentationType: '',
    topics: [],
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    affiliation: '',
    abstractTitle: '',
    presentationType: 'plenary',
    topics: [],
    mathAnswer: '',
    supportingFiles: null
  });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const [importantDates, setImportantDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);

  // ── Fetch topics ──
  useEffect(() => {
    const fetchTopics = async () => {
      if (!eventId) {
        setTopics([]);
        setLoadingTopics(false);
        return;
      }
      try {
        const response = await axios.get(`/api/topics-of-interest?eventId=${eventId}`);
        if (response.data.success && response.data.data) {
          setTopics(response.data.data);
        } else {
          setTopics([]);
        }
      } catch (error) {
        console.error('Error fetching topics:', error);
        toast.error('Could not load topics');
        setTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    };
    fetchTopics();
  }, [eventId]);

  // ── Fetch important dates ──
  useEffect(() => {
  const fetchImportantDates = async () => {
    if (!eventId) {
      setImportantDates([]);
      setLoadingDates(false);
      return;
    }

    try {
      setLoadingDates(true);
      const { data } = await axios.get(`${API_BASE}/api/programme/event/${eventId}`);

      if (data.success && data.data) {
        let programmeData = data.data;
        if (Array.isArray(programmeData) && programmeData.length > 0) {
          programmeData = programmeData[0];
        }

        const rawKeyDates = programmeData?.keyDates || [];

        const deadlines = rawKeyDates
          .filter((d) => d.isEnabled && d.date)
          .map((d) => ({
            date: new Date(d.date),
            label: d.title,
          }));

        // Sort by date (earliest first)
        deadlines.sort((a, b) => a.date - b.date);

        const formatted = deadlines.map((d) => ({
          date: d.date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          label: d.label,
        }));

        setImportantDates(formatted);
      } else {
        setImportantDates([]);
      }
    } catch (error) {
      console.error("Error fetching programme key dates:", error);
      setImportantDates([]);
    } finally {
      setLoadingDates(false);
    }
  };

  fetchImportantDates();
}, [eventId]);

  // ── Handlers ──
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'supportingFiles' && files && files.length > 0) {
      const file = files[0];
      const isAccepted = ACCEPTED_FILE_TYPES.includes(file.type) || /\.(pdf|doc|docx)$/i.test(file.name);
      if (isAccepted) {
        setFormData(prev => ({ ...prev, [name]: file }));
        setUploadedFile(file);
        setFileError('');
      } else {
        toast.error('Only PDF or Word (.doc, .docx) files are allowed');
        setFileError('Only PDF or Word (.doc, .docx) files are allowed');
        e.target.value = '';
        setUploadedFile(null);
        setFormData(prev => ({ ...prev, [name]: null }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTopicsChange = (selectedIds) => {
    setFormData(prev => ({ ...prev, topics: selectedIds }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.abstractTitle) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (formData.topics.length === 0) {
      toast.error('Please select at least one topic');
      return;
    }
    if (!formData.supportingFiles) {
      toast.error('Please upload a supporting document (PDF or Word)');
      setFileError('Please upload a supporting document');
      return;
    }
    if (parseInt(formData.mathAnswer) !== 19) {
      toast.error('Please solve the math question correctly');
      return;
    }
    if (!agreed) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('affiliation', formData.affiliation);
      submitData.append('abstractTitle', formData.abstractTitle);
      submitData.append('presentationType', formData.presentationType);
      submitData.append('mathAnswer', parseInt(formData.mathAnswer));
      submitData.append('agreed', agreed);
      submitData.append('topics', JSON.stringify(formData.topics));
      if (eventId) {
        submitData.append('eventId', eventId);
      }
      if (formData.supportingFiles) {
        submitData.append('supportingFiles', formData.supportingFiles);
      }

      const response = await axios.post('/api/abstracts/submit', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Abstract submitted successfully!');
        const selectedTopicNames = topics
          .filter(t => formData.topics.includes(t._id))
          .map(t => t.name);
        setSubmissionData({
          fullName: formData.fullName,
          email: formData.email,
          abstractTitle: formData.abstractTitle,
          presentationType: formData.presentationType,
          topics: selectedTopicNames,
        });
        setSubmitted(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          affiliation: '',
          abstractTitle: '',
          presentationType: 'plenary',
          topics: [],
          mathAnswer: '',
          supportingFiles: null
        });
        setUploadedFile(null);
        setFileError('');
        setAgreed(false);
        const fileInput = document.getElementById('supportingFiles');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(response.data.message || 'Submission failed.');
      }
    } catch (error) {
      console.error('Submission error (full):', error);
      if (error.response) {
        console.error('Server response data:', error.response.data);
        console.error('Server response status:', error.response.status);
        console.error('Server response headers:', error.response.headers);
      }

      const errMsg = error.response?.data?.message || error.response?.data?.error || 'Server error. Please try again.';
      const duplicateKeywords = ['duplicate', 'already', 'exists', 'unique', 'conflict', 'taken', 'wait 24 hours'];
      const isDuplicate = duplicateKeywords.some(keyword =>
        errMsg.toLowerCase().includes(keyword)
      );

      if (isDuplicate) {
        toast.error('You have already submitted an abstract for this event. Please check your submission or wait 24 hours.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetSubmission = () => {
    setSubmitted(false);
    setSubmissionData({ fullName: '', email: '', abstractTitle: '', presentationType: '', topics: [] });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const TEMPLATE_URL = '/templates/sample-abstract.doc';
  const handleDownloadTemplate = () => {
    const a = document.createElement('a');
    a.href = TEMPLATE_URL;
    a.download = 'abstract-template.doc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Template downloaded successfully!');
  };

  const totalGuidelines = guidelines.length;
  const displayEventName = fetchedEventName || eventName || 'Conference';

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>
      <Toaster position="top-right" toastOptions={{
        duration: 4000,
        style: { background: T.navy, color: T.white, borderRadius: '12px' },
        success: { duration: 3000 },
        error: { duration: 4000 },
      }} />

      <section className="t-hero">
        <div className="t-hero-i">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-hero-lbl">
              <RiPresentationLine size={12} />
              Meeting21 · Call for Abstracts
            </div>
            <h1 className="t-hero-h">Abstract <em>Submission</em></h1>
            <p className="t-hero-p">
              All abstracts will be peer‑reviewed by the Scientific Committee. Selection will be based on scientific quality, originality, and relevance to the conference theme.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st"><span className="t-st-n">{totalGuidelines}</span><span className="t-st-l">Guidelines</span></div>
            <div className="t-st"><span className="t-st-n">500</span><span className="t-st-l">Words Max</span></div>
            <div className="t-st"><span className="t-st-n">2027</span><span className="t-st-l">Edition</span></div>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(12px,4vw,64px) ' + 'clamp(40px,6vw,80px)' }}>

        <div className="t-grid-2">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="t-card">
            <div className="t-card-header">
              <div className="t-card-icon"><RiFileListLine size={20} style={{ color: T.primary }} /></div>
              <h2 className="t-card-title">Submission Guidelines</h2>
            </div>
            <div className="t-guidelines-grid">
              {guidelines.map((guide, index) => (
                <div key={index} style={{ display: 'flex', gap: 10 }}>
                  <guide.icon size={18} style={{ color: T.primary, marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <h3 style={{ fontSize: 'clamp(13px,1.2vw,15px)', fontWeight: 700, color: T.text, marginBottom: 3 }}>{guide.title}</h3>
                    <p style={{ fontSize: 'clamp(12px,1vw,13px)', color: T.textMuted, lineHeight: 1.4 }}>{guide.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="t-card">
            <div className="t-card-header">
              <div className="t-card-icon"><RiCalendarEventLine size={20} style={{ color: T.primary }} /></div>
              <h2 className="t-card-title">Important Dates</h2>
            </div>
            <div className="t-dates-list">
              {loadingDates ? (
                <div className="text-center py-4">
                  <div className="t-spinner" style={{ borderColor: `${T.border} transparent transparent transparent`, width: 28, height: 28 }} />
                </div>
              ) : importantDates.length === 0 ? (
                <p style={{ color: T.textMuted, textAlign: 'center', padding: '12px 0' }}>
                  No important dates have been set for this event.
                </p>
              ) : (
                importantDates.map((date, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'clamp(8px,1vw,10px) 0',
                      borderBottom: index < importantDates.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                      gap: 8,
                      flexWrap: 'wrap'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 120 }}>
                      <RiCalendarEventLine size={16} style={{ color: T.primary, flexShrink: 0 }} />
                      <span style={{ fontSize: 'clamp(12px,1vw,14px)', color: T.text, fontWeight: 500 }}>
                        {date.label}
                      </span>
                    </div>
                    <span style={{ fontSize: 'clamp(12px,1vw,14px)', fontWeight: 700, color: T.textMuted, whiteSpace: 'nowrap' }}>
                      {date.date}
                    </span>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="t-card" style={{ marginBottom: 'clamp(32px,4vw,48px)' }}>
          <div className="t-card-header">
            <div className="t-card-icon"><RiMicroscopeLine size={20} style={{ color: T.primary }} /></div>
            <h2 className="t-card-title">Topics of Interest</h2>
          </div>
          {loadingTopics ? (
            <div style={{ textAlign: 'center', padding: '16px' }}><div className="t-spinner" style={{ borderColor: `${T.border} transparent transparent transparent`, width: 28, height: 28 }} /></div>
          ) : topics.length === 0 ? (
            <p style={{ color: T.textMuted, textAlign: 'center', padding: '12px 0' }}>No topics available for this event.</p>
          ) : (
            <div className="t-topics-wrap">
              {topics.map((topic) => (
                <span key={topic._id} className="t-topic-tag">{topic.name}</span>
              ))}
            </div>
          )}
        </motion.div>

        <div style={{ textAlign: 'center', marginBottom: 'clamp(24px,3vw,32px)' }}>
          <motion.button onClick={handleDownloadTemplate} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="t-btn-outline">
            <RiDownloadLine size={16} /> Download Abstract Template
          </motion.button>
        </div>

        {submitted ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="t-card" style={{ padding: 'clamp(24px,4vw,40px)', border: `2px solid ${T.primary}` }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 'clamp(56px,6vw,64px)', height: 'clamp(56px,6vw,64px)', borderRadius: '50%', background: T.gradSuccess, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <RiCheckLine size={32} color={T.white} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px,2.5vw,28px)', fontWeight: 800, color: T.text, marginBottom: 4 }}>Your Abstract Submission is Successful</h2>
              <p style={{ fontSize: 'clamp(14px,1.2vw,16px)', color: T.textMid }}>Hello <strong>{submissionData.fullName}</strong>,</p>
            </div>
            <div style={{ background: T.offWhite, borderRadius: 12, padding: 'clamp(14px,2vw,20px)', marginBottom: 20 }}>
              <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMid }}>We have successfully received your abstract submission.</p>
            </div>
            <div className="t-success-grid" style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.text }}>
              <div><strong>Conference:</strong> {displayEventName}</div>
              <div><strong>Title:</strong> {submissionData.abstractTitle}</div>
              <div><strong>Full Name:</strong> {submissionData.fullName}</div>
              <div><strong>Email:</strong> {submissionData.email}</div>
              <div><strong>Presentation:</strong> {submissionData.presentationType?.charAt(0).toUpperCase() + submissionData.presentationType?.slice(1)}</div>
              <div><strong>Topics:</strong> {submissionData.topics?.join(', ') || 'None'}</div>
              <div><strong>Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div style={{ marginTop: 'clamp(20px,2.5vw,24px)', textAlign: 'center' }}>
              <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted, marginBottom: 14 }}>
                You will receive notification in <strong>24hrs</strong>.
              </p>
              <button onClick={resetSubmission} className="t-btn-primary">Submit Another Abstract</button>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="t-card" style={{ padding: 'clamp(20px,4vw,44px)' }}>
            <div style={{ marginBottom: 'clamp(24px,3vw,36px)' }}>
              <h2 style={{ fontSize: 'clamp(22px,2.5vw,28px)', fontWeight: 800, color: T.text, marginBottom: 6 }}>Submit Your Abstract</h2>
              <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted }}>All fields marked with <span className="t-required">*</span> are required</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="t-form-2">
                <div>
                  <label className="t-form-label">Full Name <span className="t-required">*</span></label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="Dr. John Smith" />
                </div>
                <div>
                  <label className="t-form-label">Email Address <span className="t-required">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="john.smith@university.edu" />
                </div>
              </div>

              <div className="t-form-2">
                <div>
                  <label className="t-form-label">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="+1 (555) 123-4567" />
                </div>
                <div>
                  <label className="t-form-label">Affiliation/Institution</label>
                  <input type="text" name="affiliation" value={formData.affiliation} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="Harvard Medical School" />
                </div>
              </div>

              <div style={{ marginBottom: 'clamp(16px,2vw,24px)' }}>
                <label className="t-form-label">Abstract Title <span className="t-required">*</span></label>
                <input type="text" name="abstractTitle" value={formData.abstractTitle} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="A concise and descriptive title" />
              </div>

              <div style={{ marginBottom: 'clamp(16px,2vw,24px)' }}>
                <label className="t-form-label">Presentation Type <span className="t-required">*</span></label>
                <select name="presentationType" value={formData.presentationType} onChange={handleChange} disabled={loading} className="t-form-input" style={{ cursor: 'pointer' }}>
                  <option value="plenary">Plenary</option>
                  <option value="keynote">Keynote</option>
                  <option value="invited">Invited</option>
                  <option value="oral">Oral</option>
                  <option value="poster">Poster</option>
                </select>
              </div>

              <div style={{ marginBottom: 'clamp(16px,2vw,24px)' }}>
                <label className="t-form-label">Select Topics of Interest <span className="t-required">*</span></label>
                {loadingTopics ? (
                  <div style={{ padding: '10px', color: T.textMuted }}>Loading topics...</div>
                ) : topics.length === 0 ? (
                  <div style={{ padding: '10px', color: T.textMuted }}>No topics available for this event.</div>
                ) : (
                  <MultiSelect
                    options={topics}
                    selected={formData.topics}
                    onChange={handleTopicsChange}
                    placeholder="Select topics that interest you..."
                    disabled={loading}
                  />
                )}
                {formData.topics.length === 0 && topics.length > 0 && (
                  <p style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: '#EF4444', marginTop: 4 }}>Please select at least one topic</p>
                )}
              </div>

              <div style={{ marginBottom: 'clamp(16px,2vw,24px)' }}>
                <label className="t-form-label">Upload Supporting Document (PDF or Word) <span className="t-required">*</span></label>
                <div className={`t-dropzone ${uploadedFile ? 't-dropzone-active' : ''}`} onClick={() => document.getElementById('supportingFiles').click()}>
                  <input
                    type="file"
                    id="supportingFiles"
                    name="supportingFiles"
                    onChange={handleChange}
                    accept={ACCEPTED_FILE_EXT}
                    disabled={loading}
                    style={{ display: 'none' }}
                  />
                  {uploadedFile ? (
                    <div>
                      {isWordFile(uploadedFile) ? (
                        <RiFileWordLine size={32} style={{ color: T.primary, marginBottom: 6 }} />
                      ) : (
                        <RiFilePdfLine size={32} style={{ color: T.primary, marginBottom: 6 }} />
                      )}
                      <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', fontWeight: 700, color: T.primary, marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <RiCheckLine size={16} /> {uploadedFile.name}
                      </p>
                      <p style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB · Uploaded
                      </p>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setUploadedFile(null); setFormData(prev => ({ ...prev, supportingFiles: null })); const fi = document.getElementById('supportingFiles'); if (fi) fi.value = ''; }} style={{ marginTop: 6, padding: '4px 12px', background: '#EF4444', color: 'white', border: 'none', borderRadius: 20, fontSize: 'clamp(11px,0.9vw,12px)', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ) : (
                    <div>
                      <RiUploadCloudLine size={32} style={{ color: T.textMuted, marginBottom: 6 }} />
                      <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted, marginBottom: 3 }}>Click or drag & drop to upload PDF or Word (.doc, .docx)</p>
                    </div>
                  )}
                </div>
                {fileError && (
                  <p style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: '#EF4444', marginTop: 4 }}>{fileError}</p>
                )}
              </div>

              <div style={{ marginBottom: 'clamp(16px,2vw,24px)', padding: 'clamp(14px,2vw,20px)', background: T.softTeal, borderRadius: 14, border: `1px solid ${T.borderLight}` }}>
                <label className="t-form-label">Security Check: What's 16 + 3? <span className="t-required">*</span></label>
                <input type="number" name="mathAnswer" value={formData.mathAnswer} onChange={handleChange} disabled={loading} className="t-form-input" style={{ background: T.white }} placeholder="Enter your answer" />
              </div>

              <div style={{ marginBottom: 'clamp(24px,3vw,32px)' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={loading} style={{ marginTop: 2, accentColor: T.primary, flexShrink: 0 }} />
                  <span style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted, lineHeight: 1.5 }}>
                    I confirm that this abstract is original work and has not been published or presented elsewhere.
                    I agree to all policies and terms and permit my abstract to be included in the conference proceedings
                    and website if accepted.
                  </span>
                </label>
              </div>

              <motion.button type="submit" disabled={loading} className="t-btn-primary" style={{ width: '100%' }}>
                {loading ? <><div className="t-spinner" /> Submitting...</> : <>Submit Abstract <RiSendPlaneFill size={16} /></>}
              </motion.button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}